import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatLocalizedDate } from '@/lib/localizedDate';
import { VatIncludedLabel } from '@/components/billing/PricingBreakdown';
import { formatSarAmount } from '@/lib/canonicalMoney';
import type { BillingHistoryInvoice } from '@/lib/billing/billingHistory';
import { buildCustomerInvoicePath } from '../../../supabase/functions/_shared/officialInvoiceCustomerRoute';

interface InvoiceViewerDialogProps {
  invoice: BillingHistoryInvoice | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InvoiceViewerDialog({ invoice, open, onOpenChange }: InvoiceViewerDialogProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();

  if (!invoice) return null;

  const lineItems = invoice.line_items ?? [];
  const taxName = 'VAT';
  const odooInvoiceMap = invoice.erp_invoice_map?.find((map) => !!map.external_invoice_number);
  const odooInvoiceReference = odooInvoiceMap?.external_invoice_number;
  const artifactTypes = new Set((invoice.official_artifacts ?? []).map((artifact) => artifact.artifact_type));
  const hasOfficialPdf = artifactTypes.has('official_pdf');
  const officialXmlStored = artifactTypes.has('zatca_ubl_xml') || artifactTypes.has('odoo_xml_attachment');
  const officialXmlState = officialXmlStored ? t('hire.invoiceViewer.stateStored') : t('hire.invoiceViewer.statePending');
  const emailStates = new Set((invoice.official_email_deliveries ?? []).map((delivery) => delivery.delivery_state));
  const officialEmailState = emailStates.has('sent') ? 'sent' : emailStates.has('failed') ? 'failed' : 'pending';
  const officialEmailLabel = officialEmailState === 'sent'
    ? t('hire.invoiceViewer.stateSent')
    : officialEmailState === 'failed'
      ? t('hire.invoiceViewer.stateFailed')
      : t('hire.invoiceViewer.statePending');
  const previewInvoiceNumber = odooInvoiceReference ?? invoice.invoice_number;
  const isForkSeekInvoice = invoice.owner_type === 'candidate';
  const identity = invoice.billing_identity;
  const snapshotBuyerType = String(identity?.buyer_type || '').toUpperCase();
  const buyerType = snapshotBuyerType === 'B2B' || snapshotBuyerType === 'B2C'
    ? snapshotBuyerType
    : null;
  const ownerName = identity?.legal_customer_name?.trim()
    ? identity.legal_customer_name
    : t('hire.invoiceViewer.customerFallback');
  const vatNumber = buyerType === 'B2B' && identity?.vat_number
    ? identity.vat_number
    : null;
  const additionalIdScheme = buyerType === 'B2B' && identity?.additional_id_scheme
    ? identity.additional_id_scheme.toUpperCase()
    : null;
  const additionalIdValue = buyerType === 'B2B' && identity?.additional_id_value
    ? identity.additional_id_value
    : null;
  const snapshotCrNumber = buyerType === 'B2B' && identity?.commercial_registration_number
    ? identity.commercial_registration_number
    : null;
  const crNumber = snapshotCrNumber || (additionalIdScheme === 'CRN' ? additionalIdValue : null);
  const additionalBusinessId = additionalIdValue && additionalIdScheme !== 'CRN'
    ? `${additionalIdScheme || 'ID'}: ${additionalIdValue}`
    : null;
  const invoiceTitle = buyerType === 'B2C'
    ? t('hire.invoiceViewer.simplifiedTaxInvoice')
    : buyerType === 'B2B' ? t('hire.invoiceViewer.taxInvoice') : t('hire.invoiceViewer.title');
  const portalBrand = isForkSeekInvoice ? 'ForkSeek' : 'ForkHire';

  const handleViewInvoice = () => {
    if (!hasOfficialPdf || !odooInvoiceReference) {
      toast({
        title: t('hire.invoiceViewer.pdfNotReadyTitle'),
        description: t('hire.invoiceViewer.pdfNotReadyDescription'),
      });
      return;
    }
    try {
      onOpenChange(false);
      navigate(buildCustomerInvoicePath(odooInvoiceReference));
    } catch {
      toast({
        title: t('hire.invoiceViewer.downloadFailedTitle'),
        description: t('hire.invoiceViewer.downloadFailedDescription'),
        variant: 'destructive',
      });
    }
  };

  const formatSar = (value: number | string | null | undefined) => formatSarAmount(Number(value ?? 0), 'invoice_display');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        <DialogTitle className="sr-only">{t('hire.invoiceViewer.title')}</DialogTitle>
        <DialogDescription className="sr-only">{t('hire.invoiceViewer.description')}</DialogDescription>
        {/* Action bar */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-border bg-muted/30 sticky top-0 z-10">
          <h2 className="font-display font-bold text-foreground text-sm">{t('hire.invoiceViewer.title')}</h2>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="default"
              onClick={handleViewInvoice}
              className="gap-1.5"
            >
              <FileText className="w-3.5 h-3.5" />
              {t('hire.invoiceViewer.viewInvoice')}
            </Button>
          </div>
        </div>

        {/* Customer preview; the official PDF remains the statutory artifact. */}
        <div className="p-6 sm:p-8">
          <div className="invoice-container">
            {/* Header */}
            <div className="flex justify-between items-start mb-8 pb-6" style={{ borderBottom: '3px solid hsl(var(--primary))' }}>
              <div>
                <div className="text-2xl font-extrabold text-primary tracking-tight font-display">{portalBrand}</div>
                <div className="text-[11px] text-muted-foreground mt-1">{isForkSeekInvoice ? t('hire.invoiceViewer.forkSeekTagline') : t('hire.invoiceViewer.forkHireTagline')}</div>
              </div>
              <div className="text-end">
                <h1 className="text-xl font-bold uppercase tracking-wide text-foreground">{invoiceTitle}</h1>
                <div className="font-mono text-sm text-primary mt-1">{previewInvoiceNumber}</div>
                <span className={`inline-block mt-2 px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase
                  ${invoice.status === 'paid' || invoice.status === 'issued' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' : ''}
                  ${invoice.status === 'voided' ? 'bg-destructive/10 text-destructive' : ''}
                  ${invoice.status === 'draft' ? 'bg-muted text-muted-foreground' : ''}
                `}>
                  {invoice.status === 'paid' || invoice.status === 'issued' ? t(`hire.billingInvoices.status.${invoice.status}`) : invoice.status}
                </span>
              </div>
            </div>

            {/* Meta grid */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-muted/40 rounded-lg p-4">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">{t('hire.invoiceViewer.billedTo')}</div>
                <div className="text-sm font-medium text-foreground">{ownerName}</div>
                {vatNumber && <div className="text-xs text-muted-foreground mt-0.5">VAT: {vatNumber}</div>}
                {crNumber && <div className="text-xs text-muted-foreground">CR: {crNumber}</div>}
                {additionalBusinessId && <div className="text-xs text-muted-foreground">{additionalBusinessId}</div>}
              </div>
              <div className="bg-muted/40 rounded-lg p-4">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">{t('hire.invoiceViewer.invoiceDate')}</div>
                <div className="text-sm text-foreground">{invoice.issued_at ? formatLocalizedDate(invoice.issued_at, 'MMMM dd, yyyy', i18n.language) : t('common.notAvailable')}</div>
                {invoice.paid_at && (
                  <>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1 mt-3">{t('hire.invoiceViewer.paidOn')}</div>
                    <div className="text-sm text-foreground">{formatLocalizedDate(invoice.paid_at, 'MMMM dd, yyyy', i18n.language)}</div>
                  </>
                )}
              </div>
            </div>

            {/* Line items table */}
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/60">
                    <th className="text-start text-[11px] font-semibold uppercase tracking-wider text-muted-foreground px-3 py-2.5">{t('hire.invoiceViewer.table.description')}</th>
                    <th className="text-end text-[11px] font-semibold uppercase tracking-wider text-muted-foreground px-3 py-2.5">{t('hire.invoiceViewer.table.qty')}</th>
                    <th className="text-end text-[11px] font-semibold uppercase tracking-wider text-muted-foreground px-3 py-2.5">{t('hire.invoiceViewer.table.unitPrice')}</th>
                    <th className="text-end text-[11px] font-semibold uppercase tracking-wider text-muted-foreground px-3 py-2.5">{t('hire.invoiceViewer.table.amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.length > 0 ? lineItems.map((item, i) => (
                    <tr key={i} className="border-b border-border/50">
                      <td className="px-3 py-3 text-foreground">{item.description}</td>
                      <td className="px-3 py-3 text-end font-mono text-muted-foreground">{item.quantity}</td>
                      <td className="px-3 py-3 text-end font-mono text-muted-foreground">{formatSar(item.unit_price_sar)} SAR</td>
                      <td className="px-3 py-3 text-end font-mono font-medium text-foreground">{formatSar(item.total_sar)} SAR</td>
                    </tr>
                  )) : (
                    <tr className="border-b border-border/50">
                      <td className="px-3 py-3 text-foreground">{t('hire.invoiceViewer.paymentLineItem')}</td>
                      <td className="px-3 py-3 text-end font-mono text-muted-foreground">1</td>
                      <td className="px-3 py-3 text-end font-mono text-muted-foreground">{formatSar(invoice.amount_sar)} SAR</td>
                      <td className="px-3 py-3 text-end font-mono font-medium text-foreground">{formatSar(invoice.total_sar)} SAR</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="ms-auto w-72">
              <div className="flex justify-between py-1.5 text-sm">
                <span className="text-muted-foreground">{t('hire.invoiceViewer.subtotal')}</span>
                <span className="font-mono text-foreground">{formatSar(invoice.amount_sar)} SAR</span>
              </div>
              <div className="flex justify-between py-1.5 text-sm">
                <span className="text-muted-foreground">{taxName} ({invoice.vat_rate_percent ?? 15}%)</span>
                <span className="font-mono text-foreground">{formatSar(invoice.vat_amount_sar)} SAR</span>
              </div>
              <div className="flex justify-between pt-3 mt-2 text-base font-bold" style={{ borderTop: '2px solid hsl(var(--primary))' }}>
                <span className="text-primary">{t('hire.invoiceViewer.total')}</span>
                <span className="text-end font-mono text-primary">{formatSar(invoice.total_sar)} SAR<VatIncludedLabel className="block" /></span>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-12 pt-4 border-t border-border text-center text-[11px] text-muted-foreground">
              <div className="mb-3 flex flex-wrap justify-center gap-2">
                <Badge variant={hasOfficialPdf ? 'default' : 'outline'} className="text-[10px]">
                  {t('hire.invoiceViewer.officialPdfState', { state: hasOfficialPdf ? t('hire.invoiceViewer.stateStored') : t('hire.invoiceViewer.statePending') })}
                </Badge>
                <Badge variant={officialXmlStored ? 'default' : 'outline'} className="text-[10px]">
                  {t('hire.invoiceViewer.officialXmlState', { state: officialXmlState })}
                </Badge>
                <Badge variant={officialEmailState === 'sent' ? 'default' : 'outline'} className="text-[10px]">
                  {t('hire.invoiceViewer.officialEmailState', { state: officialEmailLabel })}
                </Badge>
              </div>
              <p>{hasOfficialPdf ? t('hire.invoiceViewer.officialNotice') : t('hire.invoiceViewer.previewNotice')}</p>
              <p className="mt-1">
                {isForkSeekInvoice
                  ? t('hire.invoiceViewer.forkSeekFooterBrand')
                  : t('hire.invoiceViewer.forkHireFooterBrand')}
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
